// BloomFloraLayout.js - Flower Shop Theme
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  StoreLogo, CartBtn, TabContent, StoreFooter, InlineEditableImage,
  AWS_URL, FaHeart
} from './ThemeLayoutHelpers';

// Custom InlineEditableTextWithNav component
const InlineEditableTextWithNav = ({ p, field, value, className = "", style = {}, children, onNavigate, navPage, isInModal = false }) => {
  const isEditing = p.previewEditMode && p.activeEditField === field;
  const isEditable = typeof p.onActivateField === "function";
  
  const getCurrentValue = () => {
    if (p.storeTheme && p.storeTheme[field] !== undefined && p.storeTheme[field] !== "") {
      return p.storeTheme[field];
    }
    // Also check p.themeText if available (for preview mode)
    if (p.themeText && p.themeText[field] !== undefined && p.themeText[field] !== "") {
      return p.themeText[field];
    }
    return value;
  };
  
  const [localValue, setLocalValue] = useState(getCurrentValue());

  useEffect(() => {
    if (!isEditing) {
      setLocalValue(getCurrentValue());
    }
  }, [p.storeTheme, field, value, isEditing, p.themeText]);

  const handleActivate = (event) => {
    event.preventDefault();
    event.stopPropagation();
    const currentVal = getCurrentValue();
    setLocalValue(currentVal);
    if (typeof p.onActivateField === "function") {
      p.onActivateField(field);
    }
  };

  const handleChange = (event) => {
    const newValue = event.target.value;
    setLocalValue(newValue);
  };

  const handleSaveAndClose = () => {
    if (typeof p.onPreviewTextChange === "function") {
      p.onPreviewTextChange(field, localValue);
    }
    if (typeof p.onDeactivateField === "function") {
      p.onDeactivateField();
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      event.stopPropagation();
      handleSaveAndClose();
      if (!isInModal && onNavigate && navPage) {
        onNavigate(navPage);
      }
    }
  };

  const handleBlur = () => {
    handleSaveAndClose();
  };

  if (isEditing) {
    const inputClass = "w-full rounded-2xl border border-pink-200 bg-white/90 px-3 py-2 text-sm text-gray-900 outline-none focus:border-pink-500 focus:ring-2 focus:ring-pink-100 dark:border-pink-700 dark:bg-gray-800 dark:text-gray-100";
    const multiline = field.toLowerCase().includes("subtitle") || field.toLowerCase().includes("description") || field.toLowerCase().includes("overview") || field.toLowerCase().includes("content") || field.toLowerCase().includes("story") || field.toLowerCase().includes("tip");

    return multiline ? (
      <textarea
        autoFocus
        rows={4}
        value={localValue}
        onChange={handleChange}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        onClick={(event) => event.stopPropagation()}
        className={inputClass}
        style={{ whiteSpace: "pre-wrap", ...style }}
      />
    ) : (
      <input
        autoFocus
        type="text"
        value={localValue}
        onChange={handleChange}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        onClick={(event) => event.stopPropagation()}
        className={inputClass}
        style={{ whiteSpace: "pre-wrap", ...style }}
      />
    );
  }

  const displayValue = p.storeTheme?.[field] || p.themeText?.[field] || value;
  
  return (
    <span
      className={`${className} ${isEditable ? 'cursor-pointer inline-block' : 'inline-block'}`}
      style={{ whiteSpace: "pre-wrap", ...style }}
      onClick={isEditable ? handleActivate : undefined}
      title={p.previewEditMode ? "Edit text" : undefined}
    >
      {displayValue}
    </span>
  );
};

export default function BloomFloraLayout({ p }) {
  const T = p.storeTheme || {};
  const primary = T.primaryColor || "#e11d48";
  const secondary = T.secondaryColor || "#fb7185";
  
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeSlide, setActiveSlide] = useState(0);
  const [visibleProducts, setVisibleProducts] = useState([]);
  const [activePage, setActivePage] = useState("home");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [selectedFlower, setSelectedFlower] = useState(null);
  const [selectedPost, setSelectedPost] = useState(null);
  const [selectedOccasion, setSelectedOccasion] = useState(null);
  const [selectedBouquet, setSelectedBouquet] = useState(null);
  const [hoveredCard, setHoveredCard] = useState(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [animationComplete, setAnimationComplete] = useState(false);
  const [activeCategory, setActiveCategory] = useState("all");
  const [isPetalsFalling, setIsPetalsFalling] = useState(true);
  
  const heroRef = useRef(null);
  const [refreshKey, setRefreshKey] = useState(0);
  
  useEffect(() => {
    setRefreshKey(prev => prev + 1);
  }, [p.storeTheme]);

  // Flower hero images - DIRECTLY FROM CONFIG
  const flowerHeroImageConfigs = [
    {
      field: "themePreviewImage",
      src: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/1b9fcb9a-492f-4087-852b-307cdc75e885_aa.jpg",
    },
    {
      field: "flowerHeroImage2",
      src: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/49b4c336-74d8-4d50-8b26-cf18a022deda_bb.webp",
    },
    {
      field: "flowerHeroImage3",
      src: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/7565b5c1-7664-47fc-baa5-37f063b1e255_ff.jpg",
    },
    {
      field: "flowerHeroImage4",
      src: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/30a09888-471d-456f-8bf2-0e2b9603d7cd_cc.jpg",
    },
    {
      field: "flowerHeroImage5",
      src: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/ff470645-9c8a-467c-8cf4-be40c56bf30e_dd.webp",
    },
  ];

  // Flower Collection Data - Added imageField
  const flowerCollection = [
    // Roses
    {
      id: 1,
      nameKey: "flower1_name",
      name: "Classic Red Roses",
      priceKey: "flower1_price",
      price: 899,
      originalPriceKey: "flower1_original_price",
      originalPrice: 1299,
      imageField: "flower1_image",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/c4f8abbb-7c44-4575-82b6-f4dae91241ac_gg.jpg",
      categoryKey: "flower1_category",
      category: "Roses",
      subCategory: "Premium",
      descriptionKey: "flower1_description",
      description: "Timeless red roses in full bloom, perfect for expressing love.",
      badge: "Bestseller",
      badgeColor: "#e11d48",
      quantity: "12 stems",
      color: "Red",
      occasion: "Anniversary, Love",
      inStock: true,
      fragrance: "Sweet",
      season: "All Year"
    },
    {
      id: 2,
      nameKey: "flower2_name",
      name: "White Lily Bouquet",
      priceKey: "flower2_price",
      price: 1299,
      originalPriceKey: "flower2_original_price",
      originalPrice: 1699,
      imageField: "flower2_image",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/1b9fcb9a-492f-4087-852b-307cdc75e885_aa.jpg",
      categoryKey: "flower2_category",
      category: "Lilies",
      subCategory: "Premium",
      descriptionKey: "flower2_description",
      description: "Elegant white lilies symbolizing purity and refined beauty.",
      badge: "Premium",
      badgeColor: "#8b5cf6",
      quantity: "10 stems",
      color: "White",
      occasion: "Weddings, Sympathy",
      inStock: true,
      fragrance: "Strong",
      season: "Spring, Summer"
    },
    {
      id: 3,
      nameKey: "flower3_name",
      name: "Sunshine Sunflowers",
      priceKey: "flower3_price",
      price: 699,
      originalPriceKey: "flower3_original_price",
      originalPrice: 999,
      imageField: "flower3_image",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/fa671455-e8f2-490c-85de-e9ee505bd138_ii.webp",
      categoryKey: "flower3_category",
      category: "Sunflowers",
      subCategory: "Classic",
      descriptionKey: "flower3_description",
      description: "Bright sunflowers that bring instant joy and positivity.",
      badge: "Popular",
      badgeColor: "#f59e0b",
      quantity: "8 stems",
      color: "Yellow",
      occasion: "Birthdays, Cheer Up",
      inStock: true,
      fragrance: "Mild",
      season: "Summer"
    },
    {
      id: 4,
      nameKey: "flower4_name",
      name: "Pink Peony Bundle",
      priceKey: "flower4_price",
      price: 1599,
      originalPriceKey: "flower4_original_price",
      originalPrice: 1999,
      imageField: "flower4_image",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/cf66bd67-42b0-453e-82b5-b0671e8d1c4c_hh.webp",
      categoryKey: "flower4_category",
      category: "Peonies",
      subCategory: "Premium",
      descriptionKey: "flower4_description",
      description: "Lush pink peonies with a romantic, vintage charm.",
      badge: "Luxury",
      badgeColor: "#ec4899",
      quantity: "8 stems",
      color: "Pink",
      occasion: "Weddings, Romance",
      inStock: true,
      fragrance: "Sweet",
      season: "Spring"
    },
    {
      id: 5,
      nameKey: "flower5_name",
      name: "Wildflower Mix",
      priceKey: "flower5_price",
      price: 799,
      originalPriceKey: "flower5_original_price",
      originalPrice: 1099,
      imageField: "flower5_image",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/49b4c336-74d8-4d50-8b26-cf18a022deda_bb.webp",
      categoryKey: "flower5_category",
      category: "Mixed Bouquets",
      subCategory: "Rustic",
      descriptionKey: "flower5_description",
      description: "A charming mix of wildflowers in a rustic arrangement.",
      badge: "Garden Fresh",
      badgeColor: "#059669",
      quantity: "15 stems",
      color: "Multi",
      occasion: "Everyday, Gift",
      inStock: true,
      fragrance: "Mild",
      season: "Spring, Summer"
    },
    {
      id: 6,
      nameKey: "flower6_name",
      name: "Elegant Orchids",
      priceKey: "flower6_price",
      price: 1899,
      originalPriceKey: "flower6_original_price",
      originalPrice: 2499,
      imageField: "flower6_image",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/7565b5c1-7664-47fc-baa5-37f063b1e255_ff.jpg",
      categoryKey: "flower6_category",
      category: "Orchids",
      subCategory: "Premium",
      descriptionKey: "flower6_description",
      description: "Exotic orchids in a stunning arrangement for a touch of luxury.",
      badge: "Exclusive",
      badgeColor: "#7c3aed",
      quantity: "6 stems",
      color: "Purple",
      occasion: "Anniversary, Luxury",
      inStock: true,
      fragrance: "Subtle",
      season: "All Year"
    },
    {
      id: 7,
      nameKey: "flower7_name",
      name: "Daisy Delight",
      priceKey: "flower7_price",
      price: 599,
      originalPriceKey: "flower7_original_price",
      originalPrice: 849,
      imageField: "flower7_image",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/30a09888-471d-456f-8bf2-0e2b9603d7cd_cc.jpg",
      categoryKey: "flower7_category",
      category: "Daisies",
      subCategory: "Classic",
      descriptionKey: "flower7_description",
      description: "Cheerful daisies perfect for brightening any day.",
      badge: "Happy",
      badgeColor: "#f59e0b",
      quantity: "15 stems",
      color: "White",
      occasion: "Birthday, Get Well",
      inStock: true,
      fragrance: "Mild",
      season: "Spring, Summer"
    },
    {
      id: 8,
      nameKey: "flower8_name",
      name: "Romantic Tulips",
      priceKey: "flower8_price",
      price: 999,
      originalPriceKey: "flower8_original_price",
      originalPrice: 1399,
      imageField: "flower8_image",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/ff470645-9c8a-467c-8cf4-be40c56bf30e_dd.webp",
      categoryKey: "flower8_category",
      category: "Tulips",
      subCategory: "Premium",
      descriptionKey: "flower8_description",
      description: "Beautiful tulips expressing perfect and deep love.",
      badge: "Romantic",
      badgeColor: "#e11d48",
      quantity: "10 stems",
      color: "Red",
      occasion: "Valentine, Anniversary",
      inStock: true,
      fragrance: "Mild",
      season: "Spring"
    },
    {
      id: 9,
      nameKey: "flower9_name",
      name: "Lavender Bunch",
      priceKey: "flower9_price",
      price: 749,
      originalPriceKey: "flower9_original_price",
      originalPrice: 999,
      imageField: "flower9_image",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/cf66bd67-42b0-453e-82b5-b0671e8d1c4c_hh.webp",
      categoryKey: "flower9_category",
      category: "Lavender",
      subCategory: "Dried",
      descriptionKey: "flower9_description",
      description: "Calming lavender bunch perfect for relaxation and decoration.",
      badge: "Aromatic",
      badgeColor: "#8b5cf6",
      quantity: "20 stems",
      color: "Purple",
      occasion: "Relaxation, Home Decor",
      inStock: true,
      fragrance: "Strong",
      season: "All Year"
    },
    {
      id: 10,
      nameKey: "flower10_name",
      name: "Garden Rose Box",
      priceKey: "flower10_price",
      price: 2199,
      originalPriceKey: "flower10_original_price",
      originalPrice: 2799,
      imageField: "flower10_image",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/656416ab-1cac-485b-afab-17cd1e80b570_ww.webp",
      categoryKey: "flower10_category",
      category: "Roses",
      subCategory: "Luxury",
      descriptionKey: "flower10_description",
      description: "A stunning box of premium garden roses in a luxurious arrangement.",
      badge: "Luxury",
      badgeColor: "#e11d48",
      quantity: "24 stems",
      color: "Red",
      occasion: "Proposal, Anniversary",
      inStock: true,
      fragrance: "Sweet",
      season: "All Year"
    }
  ];

  // Categories - With editable labels
  const flowerCategories = [
    { key: "all", icon: "🌺", labelKey: "cat_all", label: "All Flowers", color: "#e11d48" },
    { key: "roses", icon: "🌹", labelKey: "cat_roses", label: "Roses", color: "#dc2626" },
    { key: "lilies", icon: "🌷", labelKey: "cat_lilies", label: "Lilies", color: "#8b5cf6" },
    { key: "tulips", icon: "🌷", labelKey: "cat_tulips", label: "Tulips", color: "#f59e0b" },
    { key: "peonies", icon: "🌸", labelKey: "cat_peonies", label: "Peonies", color: "#ec4899" },
    { key: "orchids", icon: "🌺", labelKey: "cat_orchids", label: "Orchids", color: "#7c3aed" },
    { key: "mixed", icon: "💐", labelKey: "cat_mixed", label: "Mixed", color: "#059669" },
  ];

  // Occasions - With editable labels
  const occasions = [
    { id: "romance", icon: "❤️", labelKey: "occasion_romance", label: "Romance", color: "#e11d48" },
    { id: "wedding", icon: "💒", labelKey: "occasion_wedding", label: "Weddings", color: "#8b5cf6" },
    { id: "birthday", icon: "🎂", labelKey: "occasion_birthday", label: "Birthdays", color: "#f59e0b" },
    { id: "sympathy", icon: "🕊️", labelKey: "occasion_sympathy", label: "Sympathy", color: "#64748b" },
    { id: "congrats", icon: "🎉", labelKey: "occasion_congrats", label: "Congrats", color: "#059669" },
    { id: "just-because", icon: "💝", labelKey: "occasion_justbecause", label: "Just Because", color: "#ec4899" },
  ];

  // Bouquet Styles - Added imageField
  const bouquetStyles = [
    {
      id: 1,
      nameKey: "style1_name",
      name: "Hand-Tied Bouquet",
      imageField: "style1_image",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/ff470645-9c8a-467c-8cf4-be40c56bf30e_dd.webp",
      descKey: "style1_desc",
      desc: "Classic hand-tied arrangement with natural stems",
      color: "#e11d48"
    },
    {
      id: 2,
      nameKey: "style2_name",
      name: "Round Posy",
      imageField: "style2_image",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/656416ab-1cac-485b-afab-17cd1e80b570_ww.webp",
      descKey: "style2_desc",
      desc: "Compact rounded bouquet perfect for any occasion",
      color: "#ec4899"
    },
    {
      id: 3,
      nameKey: "style3_name",
      name: "Cascading Bouquet",
      imageField: "style3_image",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/31abfdca-6f2a-4c40-b391-d3444d63a71d_99.jpg",
      descKey: "style3_desc",
      desc: "Elegant waterfall style for grand gestures",
      color: "#8b5cf6"
    },
    {
      id: 4,
      nameKey: "style4_name",
      name: "Wildflower Mix",
      imageField: "style4_image",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/d935bcb4-570a-47b4-a456-5a294040453f_22.webp",
      descKey: "style4_desc",
      desc: "Rustic charm with seasonal blooms",
      color: "#059669"
    },
  ];

  // Petals falling effect
  const petals = Array.from({ length: 30 }, (_, i) => ({
    id: i,
    left: Math.random() * 100,
    size: 5 + Math.random() * 15,
    delay: Math.random() * 10,
    duration: 8 + Math.random() * 10,
    rotation: Math.random() * 360,
    color: `hsl(${340 + Math.random() * 30}, 80%, ${60 + Math.random() * 30}%)`,
    opacity: 0.3 + Math.random() * 0.4,
  }));

  // Blog Posts - Added imageField
  const editorialPosts = [
    {
      id: 1,
      titleKey: "post1_title",
      title: "The Language of Flowers",
      descKey: "post1_desc",
      desc: "What each flower symbolizes in the language of love.",
      fullContentKey: "post1_content",
      fullContent: "Flowers have their own secret language. From the passionate red rose to the pure white lily, each bloom carries a meaning that transcends words. Discover the rich symbolism behind your favorite flowers and express your feelings without saying a word.",
      imageField: "post1_image",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/4baae98e-e5eb-4fc1-b0b5-3070cb124ea7_uu.jpg",
      dateKey: "post1_date",
      date: "March 15, 2024",
      authorKey: "post1_author",
      author: "Flora Bloom",
      category: "Flower Meanings",
      categoryKey: "post1_category"
    },
    {
      id: 2,
      titleKey: "post2_title",
      title: "Seasonal Flower Guide",
      descKey: "post2_desc",
      desc: "Which flowers bloom best in each season.",
      fullContentKey: "post2_content",
      fullContent: "From spring tulips to summer sunflowers, each season brings its own floral treasures. Our guide helps you choose the perfect blooms for every time of year, ensuring the freshest and most beautiful flowers for your special moments.",
      imageField: "post2_image",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/2c74a009-d599-4ec1-ad30-d210b23dbc53_ii.webp",
      dateKey: "post2_date",
      date: "March 10, 2024",
      authorKey: "post2_author",
      author: "Gardenia Rose",
      category: "Seasonal Guide",
      categoryKey: "post2_category"
    },
    {
      id: 3,
      titleKey: "post3_title",
      title: "Caring for Cut Flowers",
      descKey: "post3_desc",
      desc: "Tips to keep your flowers fresh for longer.",
      fullContentKey: "post3_content",
      fullContent: "Nothing beats the beauty of fresh flowers, but keeping them vibrant requires care. From trimming stems to choosing the right vase, learn the essential tips for extending the life of your floral arrangements and enjoying them for days.",
      imageField: "post3_image",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/c527029b-6de2-446f-9b27-88544833ccb7_qq.jpg",
      dateKey: "post3_date",
      date: "March 5, 2024",
      authorKey: "post3_author",
      author: "Violet Thorne",
      category: "Flower Care",
      categoryKey: "post3_category"
    }
  ];

  // Navigation items - With editable labels
  const navItems = [
    { key: "nav_home", labelKey: "nav_home", label: "HOME", page: "home" },
    { key: "nav_shop", labelKey: "nav_shop", label: "SHOP", page: "shop" },
    { key: "nav_roses", labelKey: "nav_roses", label: "ROSES", page: "roses" },
    { key: "nav_bouquets", labelKey: "nav_bouquets", label: "BOUQUETS", page: "bouquets" },
    { key: "nav_occasions", labelKey: "nav_occasions", label: "OCCASIONS", page: "occasions" },
    { key: "nav_journal", labelKey: "nav_journal", label: "JOURNAL", page: "journal" },
    { key: "nav_about", labelKey: "nav_about", label: "ABOUT", page: "about" },
  ];

  // Top bar messages - With editable fields
  const topBarMessages = [
    { key: "top_bar_message_1", value: "🌹 FREE DELIVERY ON FIRST ORDER" },
    { key: "top_bar_message_2", value: "✦ USE CODE 'BLOOM20' FOR 20% OFF" },
    { key: "top_bar_message_3", value: "💐 HAND-TIED WITH LOVE" },
    { key: "top_bar_message_4", value: "✦ 7-DAY FRESHNESS GUARANTEE" },
  ];

  // Auto-rotate hero images - USING CONFIG DIRECTLY
  useEffect(() => {
    if (p.previewEditMode) return;
    const interval = setInterval(() => {
      setActiveSlide((prev) => (prev + 1) % flowerHeroImageConfigs.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [p.previewEditMode]);

  // Animate products
  useEffect(() => {
    const products = p.filteredProducts || [];
    if (products.length === 0) return;
    
    setTimeout(() => setAnimationComplete(true), 300);
    
    products.forEach((_, index) => {
      setTimeout(() => {
        setVisibleProducts(prev => [...prev, index]);
      }, index * 80 + 500);
    });
  }, [p.filteredProducts]);

  // Mouse move for parallax
  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({
        x: (e.clientX / window.innerWidth - 0.5) * 15,
        y: (e.clientY / window.innerHeight - 0.5) * 10,
      });
    };
    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, []);

  // Scroll handler
  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navigateTo = useCallback((page, event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    if (p.previewEditMode && p.activeEditField) {
      return;
    }
    setActivePage(page);
    setMobileMenuOpen(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [p.previewEditMode, p.activeEditField]);

  const handleNavigateAfterEdit = useCallback((page) => {
    setActivePage(page);
    setMobileMenuOpen(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const openBlogModal = useCallback((post, event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    if (p.previewEditMode && p.activeEditField) {
      return;
    }
    setSelectedPost(post);
  }, [p.previewEditMode, p.activeEditField]);

  const closeBlogModal = useCallback(() => {
    setSelectedPost(null);
  }, []);

  const openOccasionModal = useCallback((occasion, event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    if (p.previewEditMode && p.activeEditField) {
      return;
    }
    setSelectedOccasion(occasion);
  }, [p.previewEditMode, p.activeEditField]);

  const closeOccasionModal = useCallback(() => {
    setSelectedOccasion(null);
  }, []);

  const openBouquetModal = useCallback((bouquet, event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    if (p.previewEditMode && p.activeEditField) {
      return;
    }
    setSelectedBouquet(bouquet);
  }, [p.previewEditMode, p.activeEditField]);

  const closeBouquetModal = useCallback(() => {
    setSelectedBouquet(null);
  }, []);

  const handleBackdropClick = useCallback((event) => {
    if (event.target === event.currentTarget) {
      if (p.previewEditMode && p.activeEditField) {
        return;
      }
      setSelectedFlower(null);
      setSelectedPost(null);
      setSelectedOccasion(null);
      setSelectedBouquet(null);
    }
  }, [p.previewEditMode, p.activeEditField]);

  const handleEditHeroImage = useCallback((event, field = null) => {
    event.preventDefault();
    event.stopPropagation();
    if (!p.previewEditMode) {
      return;
    }
    const targetField = field || flowerHeroImageConfigs[activeSlide]?.field || "themePreviewImage";
    const targetIndex = flowerHeroImageConfigs.findIndex((image) => image.field === targetField);
    if (targetIndex >= 0) {
      setActiveSlide(targetIndex);
    }
    if (typeof p.onImageFieldClick === "function") {
      p.onImageFieldClick(targetField, event);
      return;
    }
    if (typeof p.onActivateField === "function") {
      p.onActivateField(targetField);
    }
  }, [activeSlide, flowerHeroImageConfigs, p]);

  const copy = {
    eyebrow: p.storeTheme?.heroEyebrow || "BLOOM FLORA",
    title: p.storeTheme?.heroTitle || "Where Flowers Tell Your Story",
    subtitle: p.storeTheme?.heroSubtitle || "Handcrafted bouquets for every moment that matters",
    primaryCta: p.storeTheme?.primaryCtaText || "Shop Now",
    secondaryCta: p.storeTheme?.secondaryCtaText || "View Occasions",
  };

  // Get editable flowers with imageField
  const editableFlowerCollection = flowerCollection.map((flower) => ({
    ...flower,
    image: p.storeTheme?.[flower.imageField] || flower.image,
  }));

  // Get editable bouquet styles with imageField
  const editableBouquetStyles = bouquetStyles.map((style) => ({
    ...style,
    image: p.storeTheme?.[style.imageField] || style.image,
  }));

  // Get editable posts with imageField
  const editableEditorialPosts = editorialPosts.map((post) => ({
    ...post,
    image: p.storeTheme?.[post.imageField] || post.image,
  }));

  // Get filtered items based on category
  const getFilteredItems = (category) => {
    if (category === "all") return editableFlowerCollection;
    const map = {
      "roses": "Roses",
      "lilies": "Lilies",
      "tulips": "Tulips",
      "peonies": "Peonies",
      "orchids": "Orchids",
      "mixed": "Mixed Bouquets",
    };
    return editableFlowerCollection.filter(item => item.category === map[category]);
  };

  const filteredItems = getFilteredItems(activeCategory);

  // Get card entrance animation
  const getCardEntrance = (index) => {
    const directions = [
      { x: -40, y: -15, rotate: -3 },
      { x: 40, y: -15, rotate: 3 },
      { x: -40, y: 15, rotate: -2 },
      { x: 40, y: 15, rotate: 2 },
    ];
    return directions[index % directions.length];
  };

  // Helper function to get category label
  const getCategoryLabel = (key) => {
    const cat = flowerCategories.find(c => c.key === key);
    return cat ? cat.label : key;
  };

  return (
    <div key={refreshKey} className="w-full min-h-screen bg-pink-50 overflow-x-hidden" style={{ fontFamily: T.fontFamily || "'Playfair Display', serif" }}>
      
      {/* Falling Petals */}
      {isPetalsFalling && (
        <div className="fixed inset-0 pointer-events-none overflow-hidden z-0">
          {petals.map((petal) => (
            <div
              key={petal.id}
              className="absolute animate-falling-petal"
              style={{
                left: `${petal.left}%`,
                width: `${petal.size}px`,
                height: `${petal.size * 1.2}px`,
                background: petal.color,
                borderRadius: '50% 50% 50% 0',
                transform: `rotate(${petal.rotation}deg)`,
                animationDelay: `${petal.delay}s`,
                animationDuration: `${petal.duration}s`,
                opacity: petal.opacity,
              }}
            />
          ))}
        </div>
      )}
      
      {/* Top Bar - All messages editable */}
      <div className="relative bg-pink-800 text-pink-200 py-2 overflow-hidden">
        <div className="whitespace-nowrap animate-marquee">
          {topBarMessages.map((msg) => (
            <span key={msg.key} className="inline-block mx-8 text-xs tracking-wider">
              <InlineEditableTextWithNav 
                p={p} 
                field={msg.key} 
                value={msg.value}
                onNavigate={handleNavigateAfterEdit}
                navPage="home"
              />
            </span>
          ))}
        </div>
      </div>
      
      {/* Navigation - All nav labels editable */}
      <nav className={`sticky top-0 z-50 transition-all duration-500 ${isScrolled ? "bg-pink-800/95 backdrop-blur-xl shadow-lg py-2" : "bg-pink-800/80 backdrop-blur-sm py-4"}`}>
        <div className="w-full px-4 md:px-8 flex items-center justify-between">
          <div className="flex items-center gap-3 cursor-pointer" onClick={(e) => navigateTo("home", e)}>
            <StoreLogo storeInfo={p.storeInfo} border={`border-2 ${isScrolled ? "border-pink-400" : "border-pink-300"}`} />
            <span className="font-bold text-xl tracking-tight text-pink-100">
              <InlineEditableTextWithNav 
                p={p} 
                field="businessName" 
                value={p.storeInfo?.businessName || "Bloom Flora"} 
                onNavigate={handleNavigateAfterEdit}
                navPage="home"
              />
            </span>
          </div>
          
          <button 
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (!(p.previewEditMode && p.activeEditField)) {
                setMobileMenuOpen(!mobileMenuOpen);
              }
            }} 
            className="md:hidden p-2"
          >
            <svg className="w-6 h-6 text-pink-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
          
          <div className="hidden md:flex items-center gap-6">
            {navItems.map((item) => (
              <button
                key={item.key}
                onClick={(e) => navigateTo(item.page, e)}
                className={`text-sm font-medium transition-all duration-300 relative group ${activePage === item.page ? "text-pink-300" : "text-pink-100/80 hover:text-pink-200"}`}
              >
                <InlineEditableTextWithNav 
                  p={p} 
                  field={item.labelKey} 
                  value={item.label}
                  onNavigate={handleNavigateAfterEdit}
                  navPage={item.page}
                />
                <span className={`absolute -bottom-1 left-0 h-0.5 bg-pink-400 transition-all duration-300 ${activePage === item.page ? "w-full" : "w-0 group-hover:w-full"}`} />
              </button>
            ))}
          </div>
          
          <div className="flex items-center gap-3">
            <button 
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (!(p.previewEditMode && p.activeEditField)) {
                  p.handleGoToWishlist?.();
                }
              }} 
              className="relative group"
            >
              <div className="p-2 rounded-full transition-all duration-300 hover:bg-pink-700/50">
                <FaHeart className="text-lg text-pink-200 hover:text-pink-100 transition-colors" />
              </div>
              {p.wishlistCount > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-pink-500 text-white text-[10px] flex items-center justify-center font-bold animate-pulse">
                  {p.wishlistCount}
                </span>
              )}
            </button>
            <CartBtn p={p} dark={true} />
            {p.userMenuElement}
          </div>
        </div>
        
        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden absolute top-full left-0 right-0 bg-pink-800 shadow-lg py-4 px-4 flex flex-col gap-2 z-50 border-t border-pink-700">
            {navItems.map((item) => (
              <button
                key={item.key}
                onClick={(e) => navigateTo(item.page, e)}
                className={`text-sm py-2 px-3 rounded-lg text-left ${activePage === item.page ? "bg-pink-700/50 text-pink-200" : "text-pink-100/80 hover:text-pink-200"}`}
              >
                <InlineEditableTextWithNav 
                  p={p} 
                  field={item.labelKey} 
                  value={item.label}
                  onNavigate={handleNavigateAfterEdit}
                  navPage={item.page}
                />
              </button>
            ))}
          </div>
        )}
      </nav>
      
      {/* ==================== HOME PAGE ==================== */}
      {activePage === "home" && (
        <>
          {/* Hero Section - FIXED: DIRECTLY USING CONFIG IMAGES - REMOVED HERO BUTTONS */}
          <section className="relative w-full h-screen min-h-[700px] overflow-hidden -mt-[60px]">
            <div className="absolute inset-0 w-full h-full">
              {flowerHeroImageConfigs.map((img, idx) => (
                <div
                  key={img.field}
                  className={`absolute inset-0 w-full h-full transition-opacity duration-1000 ease-in-out ${
                    idx === activeSlide ? "opacity-100 z-10" : "opacity-0 z-0"
                  }`}
                >
                  <InlineEditableImage p={p} field={img.field} className="h-full w-full">
                    <img 
                      src={p.storeTheme?.[img.field] || img.src} 
                      alt={`Flowers ${idx + 1}`} 
                      className="w-full h-full object-cover object-center" 
                      style={{ 
                        transform: `translate(${mousePosition.x * 0.3}px, ${mousePosition.y * 0.3}px) scale(1.05)`, 
                        transition: "transform 0.3s ease-out" 
                      }} 
                    />
                  </InlineEditableImage>
                </div>
              ))}
              <div className="absolute inset-0 bg-gradient-to-r from-pink-900/80 via-pink-900/40 to-transparent z-20 pointer-events-none" />
              <div className="absolute inset-0 bg-gradient-to-t from-pink-50 via-transparent to-transparent z-20 pointer-events-none" />
            </div>
            
            <div className="relative z-30 h-full w-full px-4 md:px-8 flex items-center">
              <div className="max-w-3xl">
                <div className="inline-block mb-6 animate-bounce-in">
                  <div className="flex items-center gap-3 px-5 py-2.5 bg-pink-800/30 backdrop-blur-md rounded-full border border-pink-400/30">
                    <span className="w-2 h-2 rounded-full bg-pink-400 animate-ping" />
                    <span className="text-pink-200 text-xs tracking-[0.2em] uppercase">
                      <InlineEditableTextWithNav 
                        p={p} 
                        field="heroEyebrow" 
                        value={copy.eyebrow}
                        onNavigate={handleNavigateAfterEdit}
                        navPage="home"
                      />
                    </span>
                    <span className="w-2 h-2 rounded-full bg-pink-400 animate-pulse" />
                  </div>
                </div>
                <h1 className="text-5xl md:text-7xl lg:text-8xl font-bold text-white leading-[1.05] tracking-tight">
                  <InlineEditableTextWithNav 
                    p={p} 
                    field="heroTitle" 
                    value={copy.title}
                    onNavigate={handleNavigateAfterEdit}
                    navPage="home"
                  >
                    {copy.title.split(" ").map((word, i) => (
                      <span key={i} className="inline-block animate-word-pop" style={{ animationDelay: `${i * 0.15}s` }}>{word}{" "}</span>
                    ))}
                  </InlineEditableTextWithNav>
                </h1>
                <p className="mt-6 text-pink-100/90 text-lg max-w-xl animate-slide-right">
                  <InlineEditableTextWithNav 
                    p={p} 
                    field="heroSubtitle" 
                    value={copy.subtitle}
                    onNavigate={handleNavigateAfterEdit}
                    navPage="home"
                  />
                </p>
                <div className="mt-10 flex flex-wrap gap-4">
                  <button 
                    onClick={(e) => navigateTo("shop", e)} 
                    className="group px-8 py-4 rounded-full bg-pink-600 text-white font-semibold tracking-wide transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:shadow-pink-600/30 flex items-center gap-2 animate-slide-up"
                  >
                    <InlineEditableTextWithNav 
                      p={p} 
                      field="primaryCtaText" 
                      value={copy.primaryCta}
                      onNavigate={handleNavigateAfterEdit}
                      navPage="shop"
                    />
                    <span className="group-hover:translate-x-1 transition-transform">→</span>
                  </button>
                  <button 
                    onClick={(e) => navigateTo("occasions", e)} 
                    className="px-8 py-4 rounded-full border-2 border-pink-400/50 text-pink-200 font-semibold tracking-wide transition-all duration-300 hover:bg-pink-600/30 hover:border-pink-300 backdrop-blur-sm animate-slide-up"
                  >
                    <InlineEditableTextWithNav 
                      p={p} 
                      field="secondaryCtaText" 
                      value={copy.secondaryCta}
                      onNavigate={handleNavigateAfterEdit}
                      navPage="occasions"
                    />
                  </button>
                </div>
              </div>
            </div>
            
            <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce-slow z-30">
              <div className="flex flex-col items-center gap-1">
                <span className="text-pink-200/60 text-[10px] tracking-wider">
                  <InlineEditableTextWithNav 
                    p={p} 
                    field="scroll_text" 
                    value="SCROLL TO EXPLORE"
                    onNavigate={handleNavigateAfterEdit}
                    navPage="home"
                  />
                </span>
                <div className="w-5 h-8 border-2 border-pink-400/30 rounded-full flex justify-center">
                  <div className="w-1 h-2 bg-pink-400/60 rounded-full mt-1 animate-scroll-dot" />
                </div>
              </div>
            </div>
          </section>
          
          {/* Featured Badge - Positioned lower */}
          <div className="relative z-10 -mt-0 text-center">
            <div className="inline-flex items-center gap-3 px-6 py-3 bg-white rounded-full shadow-lg border border-pink-100">
              <span className="text-pink-600 text-sm">🌹</span>
              <span className="text-pink-900 text-xs font-semibold tracking-wider">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="collection_badge" 
                  value="FRESH FLOWERS DAILY"
                  onNavigate={handleNavigateAfterEdit}
                  navPage="home"
                />
              </span>
              <span className="text-pink-600 text-sm">🌹</span>
            </div>
          </div>
          
          {/* Flower Categories - All labels editable */}
          <section className="w-full px-4 md:px-8 py-12">
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
              {flowerCategories.map((cat, idx) => (
                <div 
                  key={cat.key} 
                  className="group cursor-pointer animate-card-fly" 
                  style={{ animationDelay: `${idx * 0.08}s` }}
                  onClick={(e) => {
                    if (cat.key === "all") {
                      navigateTo("shop", e);
                    } else {
                      setActiveCategory(cat.key);
                      navigateTo("shop", e);
                    }
                  }}
                >
                  <div 
                    className="relative overflow-hidden rounded-2xl p-4 text-center transition-all duration-500 hover:-translate-y-2 hover:shadow-xl bg-white"
                  >
                    <div className="text-3xl mb-2 transition-all duration-300 group-hover:scale-110">
                      {cat.icon}
                    </div>
                    <h3 className="font-semibold text-gray-800 text-xs">
                      <InlineEditableTextWithNav 
                        p={p} 
                        field={cat.labelKey} 
                        value={cat.label}
                        onNavigate={handleNavigateAfterEdit}
                        navPage="shop"
                      />
                    </h3>
                    <div className="mt-1 h-0.5 w-0 group-hover:w-full transition-all duration-500 mx-auto" style={{ backgroundColor: cat.color }} />
                  </div>
                </div>
              ))}
            </div>
          </section>
          
          {/* Occasions Section - All labels editable */}
          <section className="w-full px-4 md:px-8 py-8 bg-white">
            <div className="text-center mb-8">
              <span className="text-pink-700 text-sm tracking-[0.2em] uppercase">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="occasions_badge" 
                  value="For Every Occasion"
                  onNavigate={handleNavigateAfterEdit}
                  navPage="occasions"
                />
              </span>
            </div>
            <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
              {occasions.map((occasion) => (
                <div 
                  key={occasion.id} 
                  className="group cursor-pointer rounded-xl p-4 text-center transition-all duration-300 hover:-translate-y-1 hover:shadow-lg bg-pink-50"
                  onClick={() => openOccasionModal(occasion)}
                >
                  <div className="text-3xl mb-1">{occasion.icon}</div>
                  <p className="text-xs font-semibold" style={{ color: occasion.color }}>
                    <InlineEditableTextWithNav 
                      p={p} 
                      field={occasion.labelKey} 
                      value={occasion.label}
                      onNavigate={handleNavigateAfterEdit}
                      navPage="occasions"
                    />
                  </p>
                </div>
              ))}
            </div>
          </section>
          
          {/* Featured Flowers - All text editable */}
          <section className="w-full px-4 md:px-8 py-8">
            <div className="text-center mb-10">
              <span className="text-pink-700 text-sm tracking-[0.2em] uppercase">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="featured_badge" 
                  value="Our Collection"
                  onNavigate={handleNavigateAfterEdit}
                  navPage="home"
                />
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-pink-900 mt-2">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="featured_title" 
                  value="Fresh Picks"
                  onNavigate={handleNavigateAfterEdit}
                  navPage="home"
                />
              </h2>
              <div className="w-24 h-1 bg-gradient-to-r from-pink-600 to-pink-400 mx-auto mt-4 rounded-full" />
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {editableFlowerCollection.slice(0, 4).map((flower, idx) => {
                const entrance = getCardEntrance(idx);
                const isVisible = animationComplete;
                return (
                  <div 
                    key={flower.id} 
                    className={`group cursor-pointer transition-all duration-700 ${isVisible ? "opacity-100 translate-x-0 translate-y-0 rotate-0" : `opacity-0 translate-x-[${entrance.x}px] translate-y-[${entrance.y}px] rotate-[${entrance.rotate}deg]`}`}
                    onMouseEnter={() => setHoveredCard(idx)}
                    onMouseLeave={() => setHoveredCard(null)}
                    onClick={() => setSelectedFlower(flower)}
                  >
                    <div className="relative overflow-hidden rounded-2xl bg-white shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-3">
                      <div className="relative h-56 overflow-hidden bg-pink-50">
                        <InlineEditableImage p={p} field={flower.imageField} className="block h-full w-full overflow-hidden">
                          <img src={flower.image} alt={flower.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                        </InlineEditableImage>
                        {flower.badge && (
                          <div className="absolute top-2 left-2 px-2 py-0.5 rounded-full text-white text-[10px] font-bold shadow-lg" style={{ backgroundColor: flower.badgeColor }}>
                            <InlineEditableTextWithNav 
                              p={p} 
                              field={`${flower.nameKey}_badge`} 
                              value={flower.badge}
                              onNavigate={handleNavigateAfterEdit}
                              navPage="home"
                            />
                          </div>
                        )}
                        {flower.inStock && (
                          <div className="absolute top-2 right-2 bg-green-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
                            <InlineEditableTextWithNav 
                              p={p} 
                              field="in_stock_label" 
                              value="In Stock"
                              onNavigate={handleNavigateAfterEdit}
                              navPage="home"
                            />
                          </div>
                        )}
                      </div>
                      <div className="p-3">
                        <div className="flex items-center gap-1 mb-1">
                          <span className="text-xs text-gray-400">
                            <InlineEditableTextWithNav 
                              p={p} 
                              field={`${flower.nameKey}_quantity`} 
                              value={flower.quantity}
                              onNavigate={handleNavigateAfterEdit}
                              navPage="home"
                            />
                          </span>
                          <span className="text-xs text-gray-300">•</span>
                          <span className="text-xs text-gray-400">
                            <InlineEditableTextWithNav 
                              p={p} 
                              field={`${flower.nameKey}_color`} 
                              value={flower.color}
                              onNavigate={handleNavigateAfterEdit}
                              navPage="home"
                            />
                          </span>
                        </div>
                        <h3 className="font-semibold text-gray-800 text-sm line-clamp-1">
                          <InlineEditableTextWithNav 
                            p={p} 
                            field={flower.nameKey} 
                            value={flower.name}
                            onNavigate={handleNavigateAfterEdit}
                            navPage="home"
                          />
                        </h3>
                        <div className="mt-2 flex items-center justify-between">
                          <div>
                            <span className="text-lg font-bold text-pink-700">₹<InlineEditableTextWithNav 
                              p={p} 
                              field={flower.priceKey} 
                              value={flower.price.toString()}
                              onNavigate={handleNavigateAfterEdit}
                              navPage="home"
                            /></span>
                            {flower.originalPrice > flower.price && (
                              <span className="text-xs text-gray-400 line-through ml-1">₹{flower.originalPrice}</span>
                            )}
                          </div>
                          <span className="text-xs bg-pink-100 text-pink-600 px-2 py-0.5 rounded-full">
                            <InlineEditableTextWithNav 
                              p={p} 
                              field={flower.categoryKey} 
                              value={flower.category}
                              onNavigate={handleNavigateAfterEdit}
                              navPage="home"
                            />
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            
            <div className="text-center mt-10">
              <button 
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (!(p.previewEditMode && p.activeEditField)) {
                    setActiveCategory("all");
                    setActivePage("shop");
                    setMobileMenuOpen(false);
                    window.scrollTo({ top: 0, behavior: "smooth" });
                  }
                }} 
                className="px-8 py-3 rounded-full bg-pink-700 text-white font-semibold hover:bg-pink-800 transition-all duration-300 hover:scale-105"
              >
                <InlineEditableTextWithNav 
                  p={p} 
                  field="view_all_button" 
                  value="View All Flowers"
                  onNavigate={handleNavigateAfterEdit}
                  navPage="shop"
                />
              </button>
            </div>
          </section>
          
          {/* Bouquet Styles - All text editable */}
          <section className="w-full px-4 md:px-8 py-8 bg-white">
            <div className="text-center mb-8">
              <span className="text-pink-700 text-sm tracking-[0.2em] uppercase">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="bouquet_badge" 
                  value="Bouquet Styles"
                  onNavigate={handleNavigateAfterEdit}
                  navPage="bouquets"
                />
              </span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {editableBouquetStyles.map((style) => (
                <div 
                  key={style.id} 
                  className="group cursor-pointer rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2"
                  onClick={() => openBouquetModal(style)}
                >
                  <div className="h-48 overflow-hidden">
                    <InlineEditableImage p={p} field={style.imageField} className="block h-full w-full overflow-hidden">
                      <img src={style.image} alt={style.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                    </InlineEditableImage>
                  </div>
                  <div className="p-3 text-center bg-white">
                    <h4 className="font-semibold text-gray-800" style={{ color: style.color }}>
                      <InlineEditableTextWithNav 
                        p={p} 
                        field={style.nameKey} 
                        value={style.name}
                        onNavigate={handleNavigateAfterEdit}
                        navPage="bouquets"
                      />
                    </h4>
                    <p className="text-xs text-gray-500">
                      <InlineEditableTextWithNav 
                        p={p} 
                        field={style.descKey} 
                        value={style.desc}
                        onNavigate={handleNavigateAfterEdit}
                        navPage="bouquets"
                      />
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </section>
        </>
      )}
      
      {/* ==================== SHOP PAGE ==================== */}
      {activePage === "shop" && (
        <section className="w-full px-4 md:px-8 py-12">
          <div className="text-center mb-12">
            <span className="text-pink-700 text-sm tracking-[0.2em] uppercase">
              <InlineEditableTextWithNav 
                p={p} 
                field="shop_badge" 
                value="Our Flowers"
                onNavigate={handleNavigateAfterEdit}
                navPage="shop"
              />
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-pink-900 mt-2">
              <InlineEditableTextWithNav 
                p={p} 
                field="shop_title" 
                value="Fresh Flowers"
                onNavigate={handleNavigateAfterEdit}
                navPage="shop"
              />
            </h2>
            <p className="text-gray-600 mt-3 max-w-2xl mx-auto">
              <InlineEditableTextWithNav 
                p={p} 
                field="shop_subtitle" 
                value="Beautiful blooms for every occasion"
                onNavigate={handleNavigateAfterEdit}
                navPage="shop"
              />
            </p>
            <div className="w-24 h-1 bg-gradient-to-r from-pink-600 to-pink-400 mx-auto mt-4 rounded-full" />
          </div>
          
          {/* Category Filter - All labels editable */}
          <div className="flex flex-wrap justify-center gap-2 mb-8">
            {flowerCategories.map((cat) => (
              <button
                key={cat.key}
                onClick={() => setActiveCategory(cat.key)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                  activeCategory === cat.key 
                    ? "bg-pink-700 text-white shadow-lg" 
                    : "bg-white text-gray-600 hover:bg-pink-50 hover:text-pink-700"
                }`}
              >
                {cat.icon} <InlineEditableTextWithNav 
                  p={p} 
                  field={cat.labelKey} 
                  value={cat.label}
                  onNavigate={handleNavigateAfterEdit}
                  navPage="shop"
                />
              </button>
            ))}
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {filteredItems.map((flower) => (
              <div 
                key={flower.id} 
                className="group cursor-pointer" 
                onClick={() => setSelectedFlower(flower)}
              >
                <div className="relative overflow-hidden rounded-2xl bg-white shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-3">
                  <div className="relative h-56 overflow-hidden bg-pink-50">
                    <InlineEditableImage p={p} field={flower.imageField} className="block h-full w-full overflow-hidden">
                      <img src={flower.image} alt={flower.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                    </InlineEditableImage>
                    {flower.badge && (
                      <div className="absolute top-2 left-2 px-2 py-0.5 rounded-full text-white text-[10px] font-bold shadow-lg" style={{ backgroundColor: flower.badgeColor }}>
                        <InlineEditableTextWithNav 
                          p={p} 
                          field={`${flower.nameKey}_badge`} 
                          value={flower.badge}
                          onNavigate={handleNavigateAfterEdit}
                          navPage="shop"
                        />
                      </div>
                    )}
                  </div>
                  <div className="p-3">
                    <div className="flex items-center gap-1 mb-1">
                      <span className="text-xs text-gray-400">
                        <InlineEditableTextWithNav 
                          p={p} 
                          field={`${flower.nameKey}_quantity`} 
                          value={flower.quantity}
                          onNavigate={handleNavigateAfterEdit}
                          navPage="shop"
                        />
                      </span>
                      <span className="text-xs text-gray-300">•</span>
                      <span className="text-xs text-gray-400">
                        <InlineEditableTextWithNav 
                          p={p} 
                          field={`${flower.nameKey}_color`} 
                          value={flower.color}
                          onNavigate={handleNavigateAfterEdit}
                          navPage="shop"
                        />
                      </span>
                    </div>
                    <h3 className="font-semibold text-gray-800 text-sm">
                      <InlineEditableTextWithNav 
                        p={p} 
                        field={flower.nameKey} 
                        value={flower.name}
                        onNavigate={handleNavigateAfterEdit}
                        navPage="shop"
                      />
                    </h3>
                    <div className="mt-2 flex items-center justify-between">
                      <span className="text-lg font-bold text-pink-700">₹<InlineEditableTextWithNav 
                        p={p} 
                        field={flower.priceKey} 
                        value={flower.price.toString()}
                        onNavigate={handleNavigateAfterEdit}
                        navPage="shop"
                      /></span>
                      {flower.originalPrice > flower.price && (
                        <span className="text-xs text-gray-400 line-through">₹{flower.originalPrice}</span>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 mt-1 truncate">
                      <InlineEditableTextWithNav 
                        p={p} 
                        field={`${flower.nameKey}_fragrance`} 
                        value={flower.fragrance}
                        onNavigate={handleNavigateAfterEdit}
                        navPage="shop"
                      /> • <InlineEditableTextWithNav 
                        p={p} 
                        field={`${flower.nameKey}_season`} 
                        value={flower.season}
                        onNavigate={handleNavigateAfterEdit}
                        navPage="shop"
                      />
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          {filteredItems.length === 0 && (
            <div className="text-center py-12">
              <div className="text-6xl mb-4">🌸</div>
              <h3 className="text-xl font-semibold text-gray-600">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="no_products_message" 
                  value="No flowers found in this category"
                  onNavigate={handleNavigateAfterEdit}
                  navPage="shop"
                />
              </h3>
            </div>
          )}
        </section>
      )}
      
      {/* ==================== ROSES PAGE ==================== */}
      {activePage === "roses" && (
        <section className="w-full px-4 md:px-8 py-12">
          <div className="text-center mb-12">
            <span className="text-red-700 text-sm tracking-[0.2em] uppercase">
              <InlineEditableTextWithNav 
                p={p} 
                field="roses_badge" 
                value="The King of Flowers"
                onNavigate={handleNavigateAfterEdit}
                navPage="roses"
              />
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-red-900 mt-2">
              <InlineEditableTextWithNav 
                p={p} 
                field="roses_title" 
                value="Premium Roses"
                onNavigate={handleNavigateAfterEdit}
                navPage="roses"
              />
            </h2>
            <p className="text-gray-600 mt-3 max-w-2xl mx-auto">
              <InlineEditableTextWithNav 
                p={p} 
                field="roses_subtitle" 
                value="The timeless symbol of love and romance"
                onNavigate={handleNavigateAfterEdit}
                navPage="roses"
              />
            </p>
            <div className="w-24 h-1 bg-gradient-to-r from-red-600 to-red-400 mx-auto mt-4 rounded-full" />
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {editableFlowerCollection.filter(d => d.category === "Roses").map((flower) => (
              <div 
                key={flower.id} 
                className="group cursor-pointer" 
                onClick={() => setSelectedFlower(flower)}
              >
                <div className="relative overflow-hidden rounded-2xl bg-white shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-3">
                  <div className="relative h-56 overflow-hidden bg-red-50">
                    <InlineEditableImage p={p} field={flower.imageField} className="block h-full w-full overflow-hidden">
                      <img src={flower.image} alt={flower.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                    </InlineEditableImage>
                  </div>
                  <div className="p-3">
                    <div className="flex items-center gap-1 mb-1">
                      <span className="text-xs text-gray-400">
                        <InlineEditableTextWithNav 
                          p={p} 
                          field={`${flower.nameKey}_quantity`} 
                          value={flower.quantity}
                          onNavigate={handleNavigateAfterEdit}
                          navPage="roses"
                        />
                      </span>
                      <span className="text-xs text-gray-300">•</span>
                      <span className="text-xs text-gray-400">
                        <InlineEditableTextWithNav 
                          p={p} 
                          field={`${flower.nameKey}_color`} 
                          value={flower.color}
                          onNavigate={handleNavigateAfterEdit}
                          navPage="roses"
                        />
                      </span>
                    </div>
                    <h3 className="font-semibold text-gray-800 text-sm">
                      <InlineEditableTextWithNav 
                        p={p} 
                        field={flower.nameKey} 
                        value={flower.name}
                        onNavigate={handleNavigateAfterEdit}
                        navPage="roses"
                      />
                    </h3>
                    <div className="mt-2 flex items-center justify-between">
                      <span className="text-lg font-bold text-red-700">₹<InlineEditableTextWithNav 
                        p={p} 
                        field={flower.priceKey} 
                        value={flower.price.toString()}
                        onNavigate={handleNavigateAfterEdit}
                        navPage="roses"
                      /></span>
                      {flower.originalPrice > flower.price && (
                        <span className="text-xs text-gray-400 line-through">₹{flower.originalPrice}</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
      
      {/* ==================== BOUQUETS PAGE ==================== */}
      {activePage === "bouquets" && (
        <section className="w-full px-4 md:px-8 py-12">
          <div className="text-center mb-12">
            <span className="text-purple-700 text-sm tracking-[0.2em] uppercase">
              <InlineEditableTextWithNav 
                p={p} 
                field="bouquets_page_badge" 
                value="Handcrafted"
                onNavigate={handleNavigateAfterEdit}
                navPage="bouquets"
              />
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-purple-900 mt-2">
              <InlineEditableTextWithNav 
                p={p} 
                field="bouquets_page_title" 
                value="Bouquet Styles"
                onNavigate={handleNavigateAfterEdit}
                navPage="bouquets"
              />
            </h2>
            <p className="text-gray-600 mt-3 max-w-2xl mx-auto">
              <InlineEditableTextWithNav 
                p={p} 
                field="bouquets_page_subtitle" 
                value="Choose the perfect style for your special moment"
                onNavigate={handleNavigateAfterEdit}
                navPage="bouquets"
              />
            </p>
            <div className="w-24 h-1 bg-gradient-to-r from-purple-600 to-purple-400 mx-auto mt-4 rounded-full" />
          </div>
          
          <div className="grid md:grid-cols-4 gap-6">
            {editableBouquetStyles.map((style) => (
              <div 
                key={style.id} 
                className="group cursor-pointer bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2"
                onClick={() => openBouquetModal(style)}
              >
                <div className="h-56 overflow-hidden">
                  <InlineEditableImage p={p} field={style.imageField} className="block h-full w-full overflow-hidden">
                    <img src={style.image} alt={style.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                  </InlineEditableImage>
                </div>
                <div className="p-5 text-center">
                  <h3 className="text-xl font-bold" style={{ color: style.color }}>
                    <InlineEditableTextWithNav 
                      p={p} 
                      field={style.nameKey} 
                      value={style.name}
                      onNavigate={handleNavigateAfterEdit}
                      navPage="bouquets"
                    />
                  </h3>
                  <p className="text-gray-500 text-sm mt-2">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field={style.descKey} 
                      value={style.desc}
                      onNavigate={handleNavigateAfterEdit}
                      navPage="bouquets"
                    />
                  </p>
                  <button className="mt-4 px-6 py-2 bg-pink-600 text-white rounded-full text-sm font-semibold hover:bg-pink-700 transition">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field="view_details_button" 
                      value="View Details"
                      onNavigate={handleNavigateAfterEdit}
                      navPage="bouquets"
                    />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
      
      {/* ==================== OCCASIONS PAGE ==================== */}
      {activePage === "occasions" && (
        <section className="w-full px-4 md:px-8 py-12">
          <div className="text-center mb-12">
            <span className="text-pink-700 text-sm tracking-[0.2em] uppercase">
              <InlineEditableTextWithNav 
                p={p} 
                field="occasions_page_badge" 
                value="For Every Moment"
                onNavigate={handleNavigateAfterEdit}
                navPage="occasions"
              />
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-pink-900 mt-2">
              <InlineEditableTextWithNav 
                p={p} 
                field="occasions_page_title" 
                value="Flowers for Every Occasion"
                onNavigate={handleNavigateAfterEdit}
                navPage="occasions"
              />
            </h2>
            <p className="text-gray-600 mt-3 max-w-2xl mx-auto">
              <InlineEditableTextWithNav 
                p={p} 
                field="occasions_page_subtitle" 
                value="Find the perfect flowers for your special moments"
                onNavigate={handleNavigateAfterEdit}
                navPage="occasions"
              />
            </p>
            <div className="w-24 h-1 bg-gradient-to-r from-pink-600 to-pink-400 mx-auto mt-4 rounded-full" />
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
            {occasions.map((occasion) => (
              <div 
                key={occasion.id} 
                className="group cursor-pointer bg-white rounded-2xl p-6 text-center shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2"
                onClick={() => openOccasionModal(occasion)}
              >
                <div className="text-6xl mb-3 group-hover:scale-110 transition-transform">{occasion.icon}</div>
                <h3 className="text-xl font-bold" style={{ color: occasion.color }}>
                  <InlineEditableTextWithNav 
                    p={p} 
                    field={occasion.labelKey} 
                    value={occasion.label}
                    onNavigate={handleNavigateAfterEdit}
                    navPage="occasions"
                  />
                </h3>
                <p className="text-gray-500 text-sm mt-2">
                  <InlineEditableTextWithNav 
                    p={p} 
                    field={`${occasion.labelKey}_desc`} 
                    value={`Find the perfect flowers for ${occasion.label.toLowerCase()}`}
                    onNavigate={handleNavigateAfterEdit}
                    navPage="occasions"
                  />
                </p>
                <button className="mt-4 px-6 py-2 bg-pink-600 text-white rounded-full text-sm font-semibold hover:bg-pink-700 transition">
                  <InlineEditableTextWithNav 
                    p={p} 
                    field="explore_button" 
                    value="Explore"
                    onNavigate={handleNavigateAfterEdit}
                    navPage="occasions"
                  />
                </button>
              </div>
            ))}
          </div>
        </section>
      )}
      
      {/* ==================== JOURNAL PAGE ==================== */}
      {activePage === "journal" && (
        <section className="w-full px-4 md:px-8 py-12">
          <div className="text-center mb-12">
            <span className="text-pink-700 text-sm tracking-[0.2em] uppercase">
              <InlineEditableTextWithNav 
                p={p} 
                field="journal_badge" 
                value="Our Journal"
                onNavigate={handleNavigateAfterEdit}
                navPage="journal"
              />
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-pink-900 mt-2">
              <InlineEditableTextWithNav 
                p={p} 
                field="journal_title" 
                value="Flower Stories"
                onNavigate={handleNavigateAfterEdit}
                navPage="journal"
              />
            </h2>
            <p className="text-gray-600 mt-3 max-w-2xl mx-auto">
              <InlineEditableTextWithNav 
                p={p} 
                field="journal_subtitle" 
                value="Stories, tips, and inspiration from our floral studio"
                onNavigate={handleNavigateAfterEdit}
                navPage="journal"
              />
            </p>
            <div className="w-24 h-1 bg-gradient-to-r from-pink-600 to-pink-400 mx-auto mt-4 rounded-full" />
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {editableEditorialPosts.map((post) => (
              <div 
                key={post.id} 
                className="group bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 cursor-pointer"
                onClick={(e) => openBlogModal(post, e)}
              >
                <div className="h-56 overflow-hidden">
                  <InlineEditableImage p={p} field={post.imageField} className="block h-full w-full overflow-hidden">
                    <img src={post.image} alt={post.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                  </InlineEditableImage>
                </div>
                <div className="p-5">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs text-pink-600 font-semibold">
                      <InlineEditableTextWithNav 
                        p={p} 
                        field={post.categoryKey} 
                        value={post.category}
                        onNavigate={handleNavigateAfterEdit}
                        navPage="journal"
                      />
                    </span>
                    <span className="text-xs text-gray-400">
                      <InlineEditableTextWithNav 
                        p={p} 
                        field={post.dateKey} 
                        value={post.date}
                        onNavigate={handleNavigateAfterEdit}
                        navPage="journal"
                      />
                    </span>
                  </div>
                  <h3 className="text-xl font-semibold text-pink-900 mb-2">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field={post.titleKey} 
                      value={post.title}
                      onNavigate={handleNavigateAfterEdit}
                      navPage="journal"
                    />
                  </h3>
                  <p className="text-gray-600 text-sm line-clamp-2">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field={post.descKey} 
                      value={post.desc}
                      onNavigate={handleNavigateAfterEdit}
                      navPage="journal"
                    />
                  </p>
                  <div className="mt-4 flex items-center justify-between">
                    <span className="text-xs text-gray-400">
                      <InlineEditableTextWithNav 
                        p={p} 
                        field="by_prefix" 
                        value="By"
                        onNavigate={handleNavigateAfterEdit}
                        navPage="journal"
                      />{' '}
                      <span className="font-semibold text-pink-800">
                        <InlineEditableTextWithNav 
                          p={p} 
                          field={post.authorKey} 
                          value={post.author}
                          onNavigate={handleNavigateAfterEdit}
                          navPage="journal"
                        />
                      </span>
                    </span>
                    <button 
                      onClick={(e) => openBlogModal(post, e)}
                      className="text-pink-700 text-sm font-semibold flex items-center gap-1 group-hover:gap-2 transition-all"
                    >
                      <InlineEditableTextWithNav 
                        p={p} 
                        field="read_more_button" 
                        value="Read More"
                        onNavigate={handleNavigateAfterEdit}
                        navPage="journal"
                      />
                      <span>→</span>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
      
      {/* ==================== ABOUT PAGE ==================== */}
      {activePage === "about" && (
        <section className="w-full px-4 md:px-8 py-12 md:py-20">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-pink-700 text-sm tracking-wider uppercase">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="about_badge" 
                  value="Our Story"
                  onNavigate={handleNavigateAfterEdit}
                  navPage="about"
                />
              </span>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-pink-900 mt-3">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="about_title" 
                  value="Where Flowers Bloom"
                  onNavigate={handleNavigateAfterEdit}
                  navPage="about"
                />
              </h2>
              <div className="w-16 h-1 bg-pink-600 mt-4 mb-6" />
              <p className="text-gray-600 leading-relaxed mb-4">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="about_para1" 
                  value="Bloom Flora was born from a love of flowers and a passion for creating beautiful moments. Every bouquet is handcrafted with care, using the freshest blooms from sustainable farms."
                  onNavigate={handleNavigateAfterEdit}
                  navPage="about"
                />
              </p>
              <p className="text-gray-600 leading-relaxed mb-6">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="about_para2" 
                  value="We believe that flowers have the power to express emotions that words cannot. Whether it's love, gratitude, sympathy, or celebration, our arrangements are designed to speak from the heart."
                  onNavigate={handleNavigateAfterEdit}
                  navPage="about"
                />
              </p>
              <div className="flex flex-wrap gap-6">
                <div>
                  <p className="text-3xl font-bold text-pink-700">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field="about_stat1_value" 
                      value="100+"
                      onNavigate={handleNavigateAfterEdit}
                      navPage="about"
                    />
                  </p>
                  <p className="text-sm text-gray-500">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field="about_stat1_label" 
                      value="Flower Varieties"
                      onNavigate={handleNavigateAfterEdit}
                      navPage="about"
                    />
                  </p>
                </div>
                <div>
                  <p className="text-3xl font-bold text-pink-700">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field="about_stat2_value" 
                      value="5+"
                      onNavigate={handleNavigateAfterEdit}
                      navPage="about"
                    />
                  </p>
                  <p className="text-sm text-gray-500">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field="about_stat2_label" 
                      value="Years of Experience"
                      onNavigate={handleNavigateAfterEdit}
                      navPage="about"
                    />
                  </p>
                </div>
                <div>
                  <p className="text-3xl font-bold text-pink-700">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field="about_stat3_value" 
                      value="5k+"
                      onNavigate={handleNavigateAfterEdit}
                      navPage="about"
                    />
                  </p>
                  <p className="text-sm text-gray-500">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field="about_stat3_label" 
                      value="Happy Customers"
                      onNavigate={handleNavigateAfterEdit}
                      navPage="about"
                    />
                  </p>
                </div>
              </div>
            </div>
            <div className="rounded-2xl overflow-hidden shadow-2xl">
              <InlineEditableImage p={p} field="flowerAboutImage" className="block h-full w-full overflow-hidden">
                <img 
                  src={p.storeTheme?.flowerAboutImage || "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/c527029b-6de2-446f-9b27-88544833ccb7_qq.jpg"} 
                  alt="About Bloom Flora" 
                  className="w-full h-full object-cover" 
                />
              </InlineEditableImage>
            </div>
          </div>
        </section>
      )}
      
      {/* ==================== PRODUCT TABS ==================== */}
      <section className="w-full px-4 md:px-8 mt-8">
        <div className="flex justify-center gap-2 flex-wrap">
          {[
            ["products", "🌷 Shop Flowers"],
            ["services", "🛠 Services"],
            ["details", "📖 About Store"]
          ].map(([key, label]) => (
            <button 
              key={key} 
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (!(p.previewEditMode && p.activeEditField)) {
                  p.setActiveTab(key);
                }
              }} 
              className={`px-5 md:px-7 py-2.5 rounded-full text-sm font-medium transition-all duration-300 ${p.activeTab === key ? "bg-pink-700 text-white shadow-lg" : "text-gray-500 hover:text-pink-700"}`}
            >
              <InlineEditableTextWithNav 
                p={p} 
                field={`tab_${key}_label`} 
                value={label}
                onNavigate={handleNavigateAfterEdit}
                navPage={activePage}
              />
            </button>
          ))}
        </div>
      </section>
      
      {/* Main Content */}
      <section className="flex-1 w-full px-4 md:px-8 py-8 md:py-12">
        <TabContent p={p} />
      </section>
      
      {/* Newsletter */}
      <section className="bg-gradient-to-r from-pink-800 to-pink-900 py-16 mt-8 w-full">
        <div className="max-w-3xl mx-auto text-center px-4">
          <span className="text-pink-300 text-sm tracking-wider uppercase">
            <InlineEditableTextWithNav 
              p={p} 
              field="newsletter_badge" 
              value="Subscribe"
              onNavigate={handleNavigateAfterEdit}
              navPage="home"
            />
          </span>
          <h3 className="text-2xl md:text-3xl font-bold text-white mt-2">
            <InlineEditableTextWithNav 
              p={p} 
              field="newsletter_title" 
              value="Join the Bloom Club"
              onNavigate={handleNavigateAfterEdit}
              navPage="home"
            />
          </h3>
          <p className="text-pink-200 text-sm mt-2">
            <InlineEditableTextWithNav 
              p={p} 
              field="newsletter_subtitle" 
              value="Get 15% off your first order + flower care tips"
              onNavigate={handleNavigateAfterEdit}
              navPage="home"
            />
          </p>
          <div className="mt-6 flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
            <input 
              type="email" 
              placeholder={p.storefrontText?.email_placeholder || "Your email address"} 
              className="flex-1 px-5 py-3 bg-white/10 border border-pink-400/30 rounded-full text-white placeholder-pink-300/50 focus:border-pink-400 outline-none transition" 
            />
            <button 
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (!(p.previewEditMode && p.activeEditField)) {
                  // Handle newsletter subscription
                }
              }}
              className="px-6 py-3 rounded-full bg-pink-600 text-white font-semibold hover:bg-pink-500 transition"
            >
              <InlineEditableTextWithNav 
                p={p} 
                field="subscribe_button" 
                value="Subscribe"
                onNavigate={handleNavigateAfterEdit}
                navPage="home"
              />
            </button>
          </div>
        </div>
      </section>
      
      <StoreFooter p={p} />
      
      {/* ==================== MODALS ==================== */}
      
      {/* Flower Modal - All text editable */}
      {selectedFlower && (
        <div 
          className="fixed inset-0 bg-black/80 backdrop-blur-md z-[100] flex items-center justify-center p-4 animate-fade-in" 
          onClick={handleBackdropClick}
        >
          <div className="max-w-md w-full bg-white rounded-2xl overflow-hidden shadow-2xl animate-slide-up" onClick={(e) => e.stopPropagation()}>
            <div className="relative h-64">
              <InlineEditableImage p={p} field={selectedFlower.imageField} className="block h-full w-full overflow-hidden">
                <img src={selectedFlower.image} alt={selectedFlower.name} className="w-full h-full object-cover" />
              </InlineEditableImage>
              <button 
                onClick={() => setSelectedFlower(null)} 
                className="absolute top-3 right-3 w-10 h-10 rounded-full bg-black/50 text-white hover:bg-pink-600 transition flex items-center justify-center text-xl"
              >
                <InlineEditableTextWithNav 
                  p={p} 
                  field="close_button" 
                  value="✕"
                  onNavigate={handleNavigateAfterEdit}
                  navPage={activePage}
                  isInModal={true}
                />
              </button>
              {selectedFlower.badge && (
                <div className="absolute top-3 left-3 px-3 py-1 rounded-full text-white text-xs font-bold shadow-lg" style={{ backgroundColor: selectedFlower.badgeColor }}>
                  <InlineEditableTextWithNav 
                    p={p} 
                    field={`${selectedFlower.nameKey}_badge`} 
                    value={selectedFlower.badge}
                    onNavigate={handleNavigateAfterEdit}
                    navPage={activePage}
                    isInModal={true}
                  />
                </div>
              )}
            </div>
            <div className="p-6">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xs text-gray-400">
                  <InlineEditableTextWithNav 
                    p={p} 
                    field={`${selectedFlower.nameKey}_quantity`} 
                    value={selectedFlower.quantity}
                    onNavigate={handleNavigateAfterEdit}
                    navPage={activePage}
                    isInModal={true}
                  />
                </span>
                <span className="text-xs text-gray-300">•</span>
                <span className="text-xs text-gray-400">
                  <InlineEditableTextWithNav 
                    p={p} 
                    field={`${selectedFlower.nameKey}_color`} 
                    value={selectedFlower.color}
                    onNavigate={handleNavigateAfterEdit}
                    navPage={activePage}
                    isInModal={true}
                  />
                </span>
                <span className="text-xs text-gray-300">•</span>
                <span className="text-xs text-gray-400">
                  <InlineEditableTextWithNav 
                    p={p} 
                    field={`${selectedFlower.nameKey}_fragrance`} 
                    value={selectedFlower.fragrance}
                    onNavigate={handleNavigateAfterEdit}
                    navPage={activePage}
                    isInModal={true}
                  />
                </span>
              </div>
              <h3 className="text-2xl font-bold text-pink-900">
                <InlineEditableTextWithNav 
                  p={p} 
                  field={selectedFlower.nameKey} 
                  value={selectedFlower.name}
                  onNavigate={handleNavigateAfterEdit}
                  navPage={activePage}
                  isInModal={true}
                />
              </h3>
              <p className="text-gray-600 text-sm mt-2">
                <InlineEditableTextWithNav 
                  p={p} 
                  field={selectedFlower.descriptionKey} 
                  value={selectedFlower.description}
                  onNavigate={handleNavigateAfterEdit}
                  navPage={activePage}
                  isInModal={true}
                />
              </p>
              <div className="mt-4 flex flex-wrap gap-2">
                <span className="text-xs bg-pink-100 text-pink-800 px-2 py-1 rounded-full">
                  <InlineEditableTextWithNav 
                    p={p} 
                    field={selectedFlower.categoryKey} 
                    value={selectedFlower.category}
                    onNavigate={handleNavigateAfterEdit}
                    navPage={activePage}
                    isInModal={true}
                  />
                </span>
                <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full">
                  <InlineEditableTextWithNav 
                    p={p} 
                    field={`${selectedFlower.nameKey}_subcategory`} 
                    value={selectedFlower.subCategory}
                    onNavigate={handleNavigateAfterEdit}
                    navPage={activePage}
                    isInModal={true}
                  />
                </span>
                <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full">
                  <InlineEditableTextWithNav 
                    p={p} 
                    field={`${selectedFlower.nameKey}_season`} 
                    value={selectedFlower.season}
                    onNavigate={handleNavigateAfterEdit}
                    navPage={activePage}
                    isInModal={true}
                  />
                </span>
              </div>
              <div className="mt-4">
                <p className="text-xs text-gray-500 font-semibold">
                  <InlineEditableTextWithNav 
                    p={p} 
                    field="perfect_for_label" 
                    value="Perfect For:"
                    onNavigate={handleNavigateAfterEdit}
                    navPage={activePage}
                    isInModal={true}
                  />
                </p>
                <p className="text-sm text-gray-700">
                  <InlineEditableTextWithNav 
                    p={p} 
                    field={`${selectedFlower.nameKey}_occasion`} 
                    value={selectedFlower.occasion}
                    onNavigate={handleNavigateAfterEdit}
                    navPage={activePage}
                    isInModal={true}
                  />
                </p>
              </div>
              <div className="mt-4 flex items-center gap-3">
                <span className="text-3xl font-bold text-pink-700">
                  ₹<InlineEditableTextWithNav 
                    p={p} 
                    field={selectedFlower.priceKey} 
                    value={selectedFlower.price.toString()}
                    onNavigate={handleNavigateAfterEdit}
                    navPage={activePage}
                    isInModal={true}
                  />
                </span>
                {selectedFlower.originalPrice > selectedFlower.price && (
                  <span className="text-sm text-gray-400 line-through">
                    ₹<InlineEditableTextWithNav 
                      p={p} 
                      field={selectedFlower.originalPriceKey} 
                      value={selectedFlower.originalPrice.toString()}
                      onNavigate={handleNavigateAfterEdit}
                      navPage={activePage}
                      isInModal={true}
                    />
                  </span>
                )}
              </div>
              <div className="mt-4 text-sm text-pink-600 text-center bg-pink-50 p-3 rounded-full border border-pink-200">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="flower_modal_message" 
                  value="🌸 A perfect bloom for your special moment"
                  onNavigate={handleNavigateAfterEdit}
                  navPage={activePage}
                  isInModal={true}
                />
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Occasion Modal */}
      {selectedOccasion && (
        <div 
          className="fixed inset-0 bg-black/80 backdrop-blur-md z-[100] flex items-center justify-center p-4 animate-fade-in" 
          onClick={handleBackdropClick}
        >
          <div className="max-w-lg w-full bg-white rounded-2xl p-8 text-center shadow-2xl animate-slide-up" onClick={(e) => e.stopPropagation()}>
            <button 
              onClick={closeOccasionModal} 
              className="float-right text-gray-400 hover:text-gray-600 text-2xl"
            >
              <InlineEditableTextWithNav 
                p={p} 
                field="close_button" 
                value="✕"
                onNavigate={handleNavigateAfterEdit}
                navPage="occasions"
                isInModal={true}
              />
            </button>
            <div className="text-7xl mb-4">{selectedOccasion.icon}</div>
            <h2 className="text-3xl font-bold text-pink-900" style={{ color: selectedOccasion.color }}>
              <InlineEditableTextWithNav 
                p={p} 
                field={selectedOccasion.labelKey} 
                value={selectedOccasion.label}
                onNavigate={handleNavigateAfterEdit}
                navPage="occasions"
                isInModal={true}
              />
            </h2>
            <p className="text-gray-600 mt-3">
              <InlineEditableTextWithNav 
                p={p} 
                field={`${selectedOccasion.labelKey}_modal_desc`} 
                value={`Find the perfect flowers for your ${selectedOccasion.label.toLowerCase()} with our curated collection.`}
                onNavigate={handleNavigateAfterEdit}
                navPage="occasions"
                isInModal={true}
              />
            </p>
            <button 
              onClick={() => {
                closeOccasionModal();
                navigateTo("shop");
              }} 
              className="mt-6 px-8 py-3 bg-pink-700 text-white rounded-full font-semibold hover:bg-pink-800 transition"
            >
              <InlineEditableTextWithNav 
                p={p} 
                field="browse_flowers_button" 
                value="Browse Flowers"
                onNavigate={handleNavigateAfterEdit}
                navPage="occasions"
                isInModal={true}
              />
            </button>
          </div>
        </div>
      )}
      
      {/* Bouquet Modal - All text editable */}
      {selectedBouquet && (
        <div 
          className="fixed inset-0 bg-black/80 backdrop-blur-md z-[100] flex items-center justify-center p-4 animate-fade-in" 
          onClick={handleBackdropClick}
        >
          <div className="max-w-md w-full bg-white rounded-2xl overflow-hidden shadow-2xl animate-slide-up" onClick={(e) => e.stopPropagation()}>
            <div className="relative h-64">
              <InlineEditableImage p={p} field={selectedBouquet.imageField} className="block h-full w-full overflow-hidden">
                <img src={selectedBouquet.image} alt={selectedBouquet.name} className="w-full h-full object-cover" />
              </InlineEditableImage>
              <button 
                onClick={closeBouquetModal} 
                className="absolute top-3 right-3 w-10 h-10 rounded-full bg-black/50 text-white hover:bg-pink-600 transition flex items-center justify-center text-xl"
              >
                <InlineEditableTextWithNav 
                  p={p} 
                  field="close_button" 
                  value="✕"
                  onNavigate={handleNavigateAfterEdit}
                  navPage="bouquets"
                  isInModal={true}
                />
              </button>
            </div>
            <div className="p-6 text-center">
              <h2 className="text-2xl font-bold" style={{ color: selectedBouquet.color }}>
                <InlineEditableTextWithNav 
                  p={p} 
                  field={selectedBouquet.nameKey} 
                  value={selectedBouquet.name}
                  onNavigate={handleNavigateAfterEdit}
                  navPage="bouquets"
                  isInModal={true}
                />
              </h2>
              <p className="text-gray-600 mt-2">
                <InlineEditableTextWithNav 
                  p={p} 
                  field={selectedBouquet.descKey} 
                  value={selectedBouquet.desc}
                  onNavigate={handleNavigateAfterEdit}
                  navPage="bouquets"
                  isInModal={true}
                />
              </p>
              <button 
                onClick={() => {
                  closeBouquetModal();
                  navigateTo("shop");
                }} 
                className="mt-6 px-8 py-3 bg-pink-700 text-white rounded-full font-semibold hover:bg-pink-800 transition"
              >
                <InlineEditableTextWithNav 
                  p={p} 
                  field="view_flowers_button" 
                  value="View Flowers"
                  onNavigate={handleNavigateAfterEdit}
                  navPage="bouquets"
                  isInModal={true}
                />
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Blog Modal - All text editable */}
      {selectedPost && (
        <div 
          className="fixed inset-0 bg-black/80 backdrop-blur-md z-[100] flex items-center justify-center p-4 animate-fade-in" 
          onClick={handleBackdropClick}
        >
          <div className="max-w-2xl w-full bg-white rounded-2xl overflow-hidden shadow-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 z-10 bg-white border-b border-gray-100 p-4 flex justify-between items-center">
              <h3 className="text-lg font-semibold text-pink-900">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="blog_modal_title" 
                  value="Flower Stories"
                  onNavigate={handleNavigateAfterEdit}
                  navPage="journal"
                  isInModal={true}
                />
              </h3>
              <button 
                onClick={closeBlogModal} 
                className="w-10 h-10 rounded-full bg-gray-100 text-gray-600 hover:bg-pink-600 hover:text-white transition flex items-center justify-center text-xl"
              >
                <InlineEditableTextWithNav 
                  p={p} 
                  field="close_button" 
                  value="✕"
                  onNavigate={handleNavigateAfterEdit}
                  navPage="journal"
                  isInModal={true}
                />
              </button>
            </div>
            <div className="p-6">
              <div className="mb-6">
                <InlineEditableImage p={p} field={selectedPost.imageField} className="block w-full overflow-hidden rounded-xl">
                  <img src={selectedPost.image} alt={selectedPost.title} className="w-full h-64 object-cover" />
                </InlineEditableImage>
              </div>
              <div className="mb-3">
                <span className="inline-block px-3 py-1 bg-pink-100 text-pink-800 text-xs rounded-full font-semibold">
                  <InlineEditableTextWithNav 
                    p={p} 
                    field={selectedPost.categoryKey} 
                    value={selectedPost.category}
                    onNavigate={handleNavigateAfterEdit}
                    navPage="journal"
                    isInModal={true}
                  />
                </span>
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-pink-900 mb-2">
                <InlineEditableTextWithNav 
                  p={p} 
                  field={selectedPost.titleKey} 
                  value={selectedPost.title}
                  onNavigate={handleNavigateAfterEdit}
                  navPage="journal"
                  isInModal={true}
                />
              </h2>
              <div className="flex flex-wrap items-center gap-3 text-sm text-gray-500 border-b border-gray-100 pb-4 mb-4">
                <span>
                  <InlineEditableTextWithNav 
                    p={p} 
                    field={selectedPost.dateKey} 
                    value={selectedPost.date}
                    onNavigate={handleNavigateAfterEdit}
                    navPage="journal"
                    isInModal={true}
                  />
                </span>
                <span>•</span>
                <span>
                  <InlineEditableTextWithNav 
                    p={p} 
                    field="by_prefix" 
                    value="By"
                    onNavigate={handleNavigateAfterEdit}
                    navPage="journal"
                    isInModal={true}
                  />{' '}
                  <span className="font-semibold text-pink-900">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field={selectedPost.authorKey} 
                      value={selectedPost.author}
                      onNavigate={handleNavigateAfterEdit}
                      navPage="journal"
                      isInModal={true}
                    />
                  </span>
                </span>
              </div>
              <div className="prose prose-lg max-w-none">
                <p className="text-gray-600 leading-relaxed whitespace-pre-wrap">
                  <InlineEditableTextWithNav 
                    p={p} 
                    field={selectedPost.fullContentKey} 
                    value={selectedPost.fullContent}
                    onNavigate={handleNavigateAfterEdit}
                    navPage="journal"
                    isInModal={true}
                  />
                </p>
              </div>
              <div className="mt-8 pt-4 border-t border-gray-100 flex justify-end">
                <button 
                  onClick={closeBlogModal} 
                  className="px-6 py-2 rounded-full bg-pink-700 text-white hover:bg-pink-800 transition"
                >
                  <InlineEditableTextWithNav 
                    p={p} 
                    field="close_button" 
                    value="Close"
                    onNavigate={handleNavigateAfterEdit}
                    navPage="journal"
                    isInModal={true}
                  />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      <style jsx>{`
        @keyframes marquee { 0% { transform: translateX(100%); } 100% { transform: translateX(-100%); } }
        .animate-marquee { animation: marquee 20s linear infinite; }
        
        @keyframes falling-petal { 0% { transform: translateY(-10vh) rotate(0deg) scale(0); opacity: 0; } 10% { opacity: 1; } 90% { opacity: 1; } 100% { transform: translateY(110vh) rotate(720deg) scale(1); opacity: 0; } }
        .animate-falling-petal { animation: falling-petal linear infinite; }
        
        @keyframes word-pop { 0% { opacity: 0; transform: scale(0.5) rotate(-8deg); } 60% { transform: scale(1.08) rotate(2deg); } 100% { opacity: 1; transform: scale(1) rotate(0); } }
        .animate-word-pop { animation: word-pop 0.6s cubic-bezier(0.34,1.2,0.64,1) forwards; opacity: 0; }
        
        @keyframes slide-right { from { opacity: 0; transform: translateX(-40px); } to { opacity: 1; transform: translateX(0); } }
        .animate-slide-right { animation: slide-right 0.7s ease-out forwards; opacity: 0; animation-fill-mode: forwards; }
        
        @keyframes bounce-in { 0% { opacity: 0; transform: scale(0.3); } 50% { transform: scale(1.05); } 70% { transform: scale(0.95); } 100% { opacity: 1; transform: scale(1); } }
        .animate-bounce-in { animation: bounce-in 0.7s cubic-bezier(0.34,1.2,0.64,1) forwards; opacity: 0; }
        
        @keyframes card-fly { 0% { opacity: 0; transform: translateY(40px) scale(0.9); } 60% { transform: translateY(-5px) scale(1.02); } 100% { opacity: 1; transform: translateY(0) scale(1); } }
        .animate-card-fly { animation: card-fly 0.6s cubic-bezier(0.34,1.2,0.64,1) forwards; opacity: 0; }
        
        @keyframes scroll-dot { 0% { transform: translateY(0); opacity: 1; } 100% { transform: translateY(12px); opacity: 0; } }
        .animate-scroll-dot { animation: scroll-dot 1.5s ease-in-out infinite; }
        
        @keyframes slide-up { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
        .animate-slide-up { animation: slide-up 0.5s ease-out forwards; opacity: 0; animation-fill-mode: forwards; }
        
        @keyframes fade-in { from { opacity: 0; } to { opacity: 1; } }
        .animate-fade-in { animation: fade-in 0.3s ease-out forwards; }
        
        @keyframes bounce { 0%,100% { transform: translateX(-50%) translateY(0); } 50% { transform: translateX(-50%) translateY(-8px); } }
        .animate-bounce-slow { animation: bounce 2s ease-in-out infinite; }
        
        @keyframes slide-up-modal { from { opacity: 0; transform: translateY(30px) scale(0.95); } to { opacity: 1; transform: translateY(0) scale(1); } }
        .animate-slide-up { animation: slide-up-modal 0.4s ease-out forwards; }
        
        .line-clamp-1 { display: -webkit-box; -webkit-line-clamp: 1; -webkit-box-orient: vertical; overflow: hidden; }
        .line-clamp-2 { display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
      `}</style>
    </div>
  );
}